<template>
  <div>
    <b-card>
      <breadcrumbs
        :breadcrumb-items="breadcrumbItems"
      />
      <DspPartnerToolbar
        v-if="Object.keys(dsppartner).length !== 0"
        :dsppartner="dsppartner"
        closing
      />
    </b-card>
    <StatsNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-top">
            <h4>{{ $t('stats.overview_statistics') }}</h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              :value="rangePicker"
              :is-loading="isLoading"
              @refresh="getStatsData"
              @input="toggleRange"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="text-center my-3"
      >
        <b-spinner label="Loading..." />
      </div>
      <b-table
        v-else-if="rows.length"
        :items="rows"
        :fields="fields"
        striped
        responsive
        small
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        show-empty
        foot-clone
        class="text-center"
      >
        <template #head(date)>
          {{ $t('stats.date') }}
        </template>
        <template #head(impressions)>
          {{ $t('stats.impressions') }}
        </template>
        <template #head(click)>
          {{ $t('stats.clicks') }}
        </template>
        <template #head(revenue)>
          {{ $t('stats.spent') }}
        </template>
        <template #head(tax)>
          {{ $t('stats.tax') }}
        </template>
        <template #cell(date)="{ item }">
          {{ getFormattedDate(new Date(item.date)) }}
        </template>
        <template #cell(impressions)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(click)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(revenue)="data">
          {{ (data.item.revenue !== undefined && data.item.revenue !== null && data.item.tax !== undefined && data.item.tax !== null) ? currency('USD', data.item.revenue - data.item.tax) : 0 }}
        </template>
        <template #cell(tax)="data">
          {{ data.value ? currency('USD', data.value) : 0 }}
        </template>
        <template #foot(date)="data">
          <strong :data-label="data.label">{{ $t('Records') }}: {{ rows.length }}</strong>
        </template>
        <template #foot(impressions)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('impressions')) }}
        </template>
        <template #foot(click)>
          {{ sum('click') }}
        </template>
        <template #foot(revenue)>
          {{ currency('USD', sumRevenueMinusTax()) }}
        </template>
        <template #foot(tax)>
          {{ currency('USD', sum('tax')) }}
        </template>
      </b-table>
      <div
        v-else
        class="text-center text-muted my-3"
      >
        {{ $t('No data for this period') }}
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate, { getFormattedDateMonth } from '@/utils/date-formatter'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import {
  BCard,
  BTable,
  VBTooltip,
  BSpinner,
} from 'bootstrap-vue'
import DspPartnerToolbar from '../DspPartnerToolbar.vue'
import StatsNavigation from './StatsNavigation.vue'
import StatsDateRangeFilter from './StatsDateRangeFilter.vue'

export default {
  name: 'OverviewStats',
  components: {
    BCard,
    BTable,
    Breadcrumbs,
    DspPartnerToolbar,
    StatsNavigation,
    BSpinner,
    StatsDateRangeFilter,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      currency: currencyFormatter,
      rangePicker: [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
      getFormattedDate,
      getFormattedDateMonth,
      rows: [],
      fields: [
        { key: 'date', label: 'date', sortable: true },
        { key: 'impressions', label: 'impressions', sortable: true },
        { key: 'click', label: 'clicks', sortable: true },
        { key: 'revenue', label: 'revenue', sortable: true },
        { key: 'tax', label: 'tax', sortable: true },
      ],
      sortBy: 'date',
      sortDesc: true,
      sortDirection: 'desc',
      dsppartner: {},
      breadcrumbItems: [],
      isLoading: false,
    }
  },
  computed: {
    selectedRangeFormated() {
      if (!this.rangePicker || !Array.isArray(this.rangePicker) || this.rangePicker.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        const today = getFormattedDate(new Date())
        return { from: today, to: today }
      }

      const [dateFrom, dateTo] = this.rangePicker
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/
      if (!dateRegex.test(dateFrom) || !dateRegex.test(dateTo)) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Format Error',
            msg: 'Invalid date format',
            variant: 'danger',
          },
          { root: true },
        )
        const today = getFormattedDate(new Date())
        return { from: today, to: today }
      }

      return {
        from: dateFrom,
        to: dateTo,
      }
    },
  },
  created() {
    this.getPartnerData()

    // Priority: URL params > saved settings > default values
    const { from, to } = this.$route.query
    if (from && to) {
      // If URL has params, use them
      this.rangePicker = [from, to]
      this.getStatsData() // Вызываем только один раз при инициализации
    } else {
      // Otherwise try to load saved settings
      this.loadSavedSettings()
      this.getStatsData() // Вызываем только один раз при инициализации
    }
  },
  methods: {
    async getPartnerData() {
      const responseData = await useJwt.getDsppartner(this.$route.params.id)
      this.dsppartner = responseData.data.dsppartner || {}
      this.setBreabcrumbs()
    },
    async getStatsData() {
      this.isLoading = true
      const { from, to } = this.selectedRangeFormated
      try {
        const response = await useJwt.getDsppartnerStatsRevenue(
          this.$route.params.id,
          {
            from,
            to,
            type: 'daily',
          },
        )
        this.rows = (response.data.items || []).map(item => ({
          ...item,
          click: item.clicks,
        }))
      } catch (error) {
        this.rows = []
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
      } finally {
        this.isLoading = false
      }
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('DSP partners'),
          to: '/dsppartners/',
        },
        {
          text: this.dsppartner.name,
          to: `/dsppartners/${this.dsppartner._id}/`,
        },
        {
          text: this.$t('Statistics'),
          active: true,
        },
      ]
    },
    sum(key) {
      return this.rows.reduce((acc, r) => acc + (r[key] || 0), 0)
    },
    sumRevenueMinusTax() {
      return this.rows.reduce((acc, r) => acc + ((r.revenue || 0) - (r.tax || 0)), 0)
    },
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString()
    },
    formatCurrency(val) {
      return `$${Number(val).toFixed(2)}`
    },
    toggleRange(selectedDates) {
      if (!selectedDates || !Array.isArray(selectedDates) || selectedDates.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        return
      }

      const [fromDate, toDate] = selectedDates
      const from = getFormattedDate(new Date(fromDate))
      const to = getFormattedDate(new Date(toDate))

      // Only update if we have valid dates
      if (from !== 'Invalid date' && to !== 'Invalid date') {
        // Check if dates actually changed
        if (this.rangePicker[0] === from && this.rangePicker[1] === to) {
          return
        }

        // Update state and URL in one go
        this.rangePicker = [from, to]
        // Обновляем URL без перезагрузки страницы
        const newQuery = { ...this.$route.query, from, to }
        const newPath = this.$route.path
        window.history.replaceState(
          {},
          '',
          `${newPath}?${new URLSearchParams(newQuery).toString()}`,
        )
        // Получаем данные
        this.getStatsData()
      } else {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Format Error',
            msg: 'Invalid date format',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    loadSavedSettings() {
      try {
        const saved = localStorage.getItem('dspStatsDateFilter')
        if (saved) {
          const settings = JSON.parse(saved)
          if (settings.rangePicker && !this.$route.query.from && !this.$route.query.to) {
            this.rangePicker = settings.rangePicker
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to load date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}

.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}
</style>
